import { useEffect, useState } from "react"
import { useNavigate, useSearchParams } from "react-router-dom"
import { getProfile } from "~/api/auth"
import type { ProfileResponse } from "~/api/auth"
import { getKibbleCurrent, markPerspectiveOpened, type KibblePerspective, type KibbleTip } from "~/api/kibble"
import { createStoryEntry, fetchStory } from "~/api/story"
import { LoggedInScreenCard } from "~/components/LoggedInScreenCard"
import { PageShell } from "~/components/PageShell"
import { DogSelector } from "~/components/DogSelector"
import { hasEntitlement, ENTITLEMENTS } from "~/utils/entitlements"
import { assetUrl } from "~/utils/assetUrl"
import { ChevronDown, ChevronUp, Lock, BookOpen, Lightbulb } from "lucide-react"

export function PerspectivesPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [profile, setProfile] = useState<ProfileResponse | null>(null)
  const [perspective, setPerspective] = useState<KibblePerspective | null>(null)
  const [tip, setTip] = useState<KibbleTip | null>(null)
  const [loading, setLoading] = useState(true)
  const [perspectiveExpanded, setPerspectiveExpanded] = useState(false)
  const [ideaExpanded, setIdeaExpanded] = useState(false)
  const [storyMessage, setStoryMessage] = useState<string | null>(null)
  const [savingStory, setSavingStory] = useState(false)
  const [savedKeys, setSavedKeys] = useState<string[]>([])

  const expandParam = searchParams.get("expand")

  useEffect(() => {
    if (expandParam === "perspective") setPerspectiveExpanded(true)
    if (expandParam === "idea") setIdeaExpanded(true)
  }, [expandParam])

  useEffect(() => {
    Promise.all([getProfile(), getKibbleCurrent()])
      .then(([profileData, kibbleData]) => {
        setProfile(profileData)
        setPerspective(kibbleData.perspective)
        setTip(kibbleData.tip)
      })
      .catch(() => navigate("/login", { replace: true }))
      .finally(() => setLoading(false))
  }, [navigate])

  useEffect(() => {
    if (!perspective || perspective.opened_at) return
    void markPerspectiveOpened(perspective.delivery_id).catch(() => {})
  }, [perspective])

  const handleDogChanged = () => {
    setLoading(true)
    setStoryMessage(null)
    Promise.all([getProfile(), getKibbleCurrent()])
      .then(([profileData, kibbleData]) => {
        setProfile(profileData)
        setPerspective(kibbleData.perspective)
        setTip(kibbleData.tip)
      })
      .catch(() => {})
      .finally(() => setLoading(false))
  }

  async function saveWeeklyToStory(
    key: string,
    entryType: "perspective" | "idea",
    title: string,
    body: string,
  ) {
    const dogId = profile?.dog_profile?.id ?? profile?.active_dog_profile_id
    if (!dogId || savingStory) return
    if (savedKeys.includes(key)) {
      setStoryMessage("Already saved to Story")
      return
    }
    setSavingStory(true)
    setStoryMessage(null)
    try {
      const story = await fetchStory(dogId)
      const alreadySaved = story.entries.some(
        (entry) => entry.entry_type === entryType && entry.title === title,
      )
      if (alreadySaved) {
        setSavedKeys((current) => (current.includes(key) ? current : [...current, key]))
        setStoryMessage("Already saved to Story")
        return
      }

      await createStoryEntry(dogId, {
        entry_type: entryType,
        title,
        body,
        occurred_at: new Date().toISOString(),
      })
      setSavedKeys((current) => [...current, key])
      setStoryMessage(`Added to ${profile?.dog_profile?.name ?? "your dog"}’s Story`)
    } catch (err) {
      setStoryMessage(err instanceof Error ? err.message : "Could not save to Story")
    } finally {
      setSavingStory(false)
    }
  }

  if (loading) {
    return (
      <PageShell>
        <p className="text-[var(--color-text-secondary)]">Loading...</p>
      </PageShell>
    )
  }

  if (!profile) return null

  const dog = profile.dog_profile
  const dogName = dog?.name ?? "Your dog"
  const personalityName = dog?.archetype_label ?? null
  const personalityIcon = dog?.archetype_icon
  const hasJournal = hasEntitlement(profile.user.entitlements, ENTITLEMENTS.dogJournal)
  const perspectiveKey = perspective
    ? `perspective:${perspective.delivery_id ?? perspective.title}`
    : ""
  const ideaKey = tip ? `idea:${tip.title}` : ""

  return (
    <PageShell innerClassName="max-w-lg w-full" fixedTabNav>
      <LoggedInScreenCard>
        <div className="flex justify-center">
          <DogSelector onDogChanged={handleDogChanged} />
        </div>

        <div className="mt-4 flex flex-col items-center text-center">
          {personalityIcon && (
            <img
              src={assetUrl(`/images/archetypes/logos/${personalityIcon}`)}
              alt={personalityName ?? "Personality"}
              className="h-14 w-14 object-contain"
            />
          )}
          <h1 className="mt-2 text-2xl font-semibold text-[var(--color-text-primary)]">
            {dogName}
          </h1>
          {personalityName && (
            <p className="text-sm text-[var(--color-text-secondary)]">{personalityName}</p>
          )}
        </div>

        <section className="mt-4">
          <button
            type="button"
            onClick={() =>
              navigate(dog?.id ? `/story?dog_id=${dog.id}` : "/story")
            }
            className="w-full rounded-xl border border-[var(--color-divider)] bg-[var(--color-bg-primary)] p-4 text-left hover:bg-[var(--color-surface-hover)]"
          >
            <div className="flex items-start gap-3">
              <div className="rounded-lg bg-[var(--color-brand-primary)]/10 p-2">
                {hasJournal ? (
                  <BookOpen size={20} className="text-[var(--color-brand-primary)]" />
                ) : (
                  <Lock size={20} className="text-[var(--color-text-secondary)]" />
                )}
              </div>
              <div className="flex-1">
                <h2 className="font-semibold text-[var(--color-text-primary)]">
                  {dogName}&apos;s Story
                </h2>
                <p className="mt-1 text-sm text-[var(--color-text-secondary)]">
                  {hasJournal
                    ? "Track memories, milestones, and special moments"
                    : "Unlock with Plus to track memories and milestones"}
                </p>
              </div>
              <ChevronDown size={20} className="shrink-0 text-[var(--color-text-secondary)]" />
            </div>
          </button>
        </section>

        {perspective ? (
          <section className="mt-4">
            <button
              type="button"
              onClick={() => setPerspectiveExpanded(!perspectiveExpanded)}
              className="w-full rounded-xl border border-[var(--color-divider)] bg-[var(--color-bg-primary)] p-4 text-left hover:bg-[var(--color-surface-hover)]"
            >
              <div className="flex items-start gap-3">
                <div className="rounded-lg bg-purple-100 p-2">
                  <BookOpen size={20} className="text-purple-600" />
                </div>
                <div className="flex-1">
                  <p className="text-xs text-[var(--color-text-secondary)]">This Week&apos;s Perspective</p>
                  <h2 className="mt-0.5 font-semibold text-[var(--color-text-primary)]">
                    {perspective.title || "Understanding Your Dog"}
                  </h2>
                </div>
                {perspectiveExpanded ? (
                  <ChevronUp size={20} className="shrink-0 text-[var(--color-text-secondary)]" />
                ) : (
                  <ChevronDown size={20} className="shrink-0 text-[var(--color-text-secondary)]" />
                )}
              </div>
            </button>
            {perspectiveExpanded && (
              <div className="mt-2 rounded-xl border border-[var(--color-divider)] bg-[var(--color-surface-card)] p-4">
                <p className="whitespace-pre-line text-sm leading-relaxed text-[var(--color-text-secondary)]">
                  {perspective.body}
                </p>
                {hasJournal && (
                  <button
                    type="button"
                    disabled={savingStory}
                    onClick={() =>
                      void saveWeeklyToStory(
                        perspectiveKey,
                        "perspective",
                        perspective.title || `This week’s perspective for ${dogName}`,
                        perspective.body,
                      )
                    }
                    className="mt-3 text-sm font-medium text-[var(--color-brand-primary)] hover:underline disabled:opacity-50"
                  >
                    ♡ Add to {dogName}&apos;s Story
                  </button>
                )}
              </div>
            )}
          </section>
        ) : (
          <section className="mt-4">
            <div className="w-full rounded-xl border border-[var(--color-divider)] bg-[var(--color-bg-primary)] p-4 text-left">
              <div className="flex items-start gap-3">
                <div className="rounded-lg bg-purple-100 p-2">
                  <BookOpen size={20} className="text-purple-600" />
                </div>
                <div className="flex-1">
                  <p className="text-xs text-[var(--color-text-secondary)]">This Week&apos;s Perspective</p>
                  <h2 className="mt-0.5 font-semibold text-[var(--color-text-primary)]">No perspective yet</h2>
                  <p className="mt-1 text-sm text-[var(--color-text-secondary)]">
                    Your first perspective will appear here after your next scheduled delivery.
                  </p>
                </div>
              </div>
            </div>
          </section>
        )}

        {tip && (
          <section className="mt-4">
            <button
              type="button"
              onClick={() => setIdeaExpanded(!ideaExpanded)}
              className="w-full rounded-xl border border-[var(--color-divider)] bg-[var(--color-bg-primary)] p-4 text-left hover:bg-[var(--color-surface-hover)]"
            >
              <div className="flex items-start gap-3">
                <div className="rounded-lg bg-amber-100 p-2">
                  <Lightbulb size={20} className="text-amber-600" />
                </div>
                <div className="flex-1">
                  <p className="text-xs text-[var(--color-text-secondary)]">This Week&apos;s Idea</p>
                  <h2 className="mt-0.5 font-semibold text-[var(--color-text-primary)]">
                    {tip.title}
                  </h2>
                </div>
                {ideaExpanded ? (
                  <ChevronUp size={20} className="shrink-0 text-[var(--color-text-secondary)]" />
                ) : (
                  <ChevronDown size={20} className="shrink-0 text-[var(--color-text-secondary)]" />
                )}
              </div>
            </button>
            {ideaExpanded && (
              <div className="mt-2 rounded-xl border border-[var(--color-divider)] bg-[var(--color-surface-card)] p-4">
                {tip.why_it_matters && (
                  <div className="mb-4">
                    <h3 className="mb-2 text-sm font-semibold text-[var(--color-text-primary)]">
                      Why it matters
                    </h3>
                    <p className="text-sm leading-relaxed text-[var(--color-text-secondary)]">
                      {tip.why_it_matters}
                    </p>
                  </div>
                )}
                {tip.how_to_do_it && (
                  <div>
                    <h3 className="mb-2 text-sm font-semibold text-[var(--color-text-primary)]">
                      How to do it
                    </h3>
                    <p className="whitespace-pre-line text-sm leading-relaxed text-[var(--color-text-secondary)]">
                      {tip.how_to_do_it}
                    </p>
                  </div>
                )}
                {hasJournal && (
                  <button
                    type="button"
                    disabled={savingStory}
                    onClick={() =>
                      void saveWeeklyToStory(
                        ideaKey,
                        "idea",
                        tip.title,
                        [tip.why_it_matters, tip.how_to_do_it].filter(Boolean).join("\n\n"),
                      )
                    }
                    className="mt-3 text-sm font-medium text-[var(--color-brand-primary)] hover:underline disabled:opacity-50"
                  >
                    ♡ Add to {dogName}&apos;s Story
                  </button>
                )}
              </div>
            )}
          </section>
        )}

        {storyMessage && (
          <p className="mt-3 text-center text-sm text-[var(--color-text-secondary)]" role="status">
            {storyMessage}
          </p>
        )}
      </LoggedInScreenCard>
    </PageShell>
  )
}
